import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Modal from '../Modal/Modal';
import { InjectAppServices } from '../../services/pure-di';
import { Loading } from '../Loading/Loading';
import { UnexpectedError } from '../shared/UnexpectedError';
import { validateRequiredField, validateSocialMediaUrl, validateUrl } from '../../validations';

const addressValidators = {
  'social-media-url': validateSocialMediaUrl,
  url: validateUrl,
};

const openZendeskChat = () => {
  if (typeof window.zE !== 'function') {
    return false;
  }

  window.zE('messenger', 'open');
  return true;
};

const InductionComponent = ({ dependencies: { appConfiguration, cdnJsonClient } }) => {
  const [inductionConfig, setInductionConfig] = useState();
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [hasConfigError, setHasConfigError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [currentStage, setCurrentStage] = useState();
  const [selectedOption, setSelectedOption] = useState();
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [previousStage, setPreviousStage] = useState();
  const [address, setAddress] = useState('');
  const [isAddressTouched, setIsAddressTouched] = useState(false);
  const intl = useIntl();
  const _ = (id, values) => intl.formatMessage({ id }, values);

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      try {
        const config = await cdnJsonClient.get(appConfiguration.inductionConfigUrl);
        if (isMounted) {
          setInductionConfig(config);
          setCurrentStage(config.initialStep);
        }
      } catch {
        if (isMounted) {
          setHasConfigError(true);
        }
      } finally {
        if (isMounted) {
          setIsConfigLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, [appConfiguration.inductionConfigUrl, cdnJsonClient]);

  if (isConfigLoading) {
    return <Loading page />;
  }

  if (hasConfigError) {
    return <UnexpectedError msgId="common.unexpected_error" />;
  }

  const channelSelectionStep = inductionConfig.steps.find(
    ({ name }) => name === 'channel-selection',
  );
  const addressStepDefinition = inductionConfig.steps.find(({ name }) => name === 'address');
  const goalsStep = inductionConfig.steps.find(({ name }) => name === 'goals');
  const selectedOptionConfig = channelSelectionStep.options.find(({ id }) => id === selectedOption);
  const addressStep = selectedOptionConfig?.nextStep;
  const addressValidator = addressValidators[addressStep?.validationMode];
  const isRequiredAddress = addressStep?.required && !!validateRequiredField(address);
  const hasInvalidAddress = !!address && !!addressValidator?.(address);
  const addressError = isRequiredAddress
    ? addressStep.i18n.requiredError
    : hasInvalidAddress
      ? addressStep.i18n.invalidError
      : null;
  const showAddressError = isAddressTouched && addressError;
  const goToNextStep = (nextStepName) => {
    setPreviousStage(currentStage);
    setCurrentStage(nextStepName);
  };
  const handleChannelSelectionAccept = () => {
    goToNextStep(selectedOptionConfig.nextStep.name);
  };
  const toggleGoal = (goal) => {
    setSelectedGoals((currentGoals) =>
      currentGoals.includes(goal)
        ? currentGoals.filter((currentGoal) => currentGoal !== goal)
        : [...currentGoals, goal],
    );
  };

  return (
    <>
      <InductionOnboarding config={inductionConfig.onboarding} formatMessage={_} />
      <Modal
        isOpen={isModalOpen}
        isCenter
        showCloseButton={false}
        type="extra-large"
        handleClose={() => setIsModalOpen(false)}
      >
        {currentStage === channelSelectionStep.name ? (
          <section className="dp-library" aria-labelledby="induction-title">
            <h2 id="induction-title" className="dp-second-order-title">
              {_(channelSelectionStep.i18n.title)}
            </h2>
            <p id="induction-description" className="dp-color-grey m-b-24">
              {_(channelSelectionStep.i18n.description)}
            </p>
            <fieldset
              aria-describedby="induction-description"
              aria-label={_(channelSelectionStep.i18n.title)}
              className="dp-rowflex m-t-30 m-b-24"
            >
              {channelSelectionStep.options.map(({ iconClass, id, i18n }) => (
                <div key={id} className="col-sm-12 col-md-6 col-lg-6 m-b-12">
                  <label className="dp-selectable-option">
                    <input
                      checked={selectedOption === id}
                      className="dp-selectable-option__input"
                      id={`induction-option-${id}`}
                      name="induction-channel"
                      onChange={() => setSelectedOption(id)}
                      type="radio"
                      value={id}
                    />
                    <span className="dp-selectable-option__content">
                      <span className="dp-icon-tile dp-icon-tile--square">
                        <i aria-hidden="true" className={iconClass} />
                      </span>
                      <span className="dp-selectable-option__label">{_(i18n.label)}</span>
                      <span className="dp-selectable-option__check" aria-hidden="true">
                        <i className="icon-checkmark" />
                      </span>
                    </span>
                  </label>
                </div>
              ))}
            </fieldset>
            <InductionFooter
              acceptDisabled={!selectedOption}
              acceptLabel={_(channelSelectionStep.i18n.accept)}
              onAccept={handleChannelSelectionAccept}
              stepLabel={_('induction.step_1_of_2')}
            />
          </section>
        ) : currentStage === addressStepDefinition.name ? (
          <section className="dp-library" aria-labelledby="induction-address-title">
            <h2 id="induction-address-title" className="dp-second-order-title">
              {_(addressStep.i18n.title)}
            </h2>
            <p id="induction-address-description" className="dp-color-grey m-b-24">
              {_(addressStep.i18n.description)}
            </p>
            <div className="dp-rowflex m-t-30 m-b-24">
              <div className="col-sm-12 col-md-8 col-lg-7">
                <div className={`awa-form${showAddressError ? ' dp-validate' : ''}`}>
                  <label htmlFor="induction-address" className="labelcontrol">
                    {_(addressStep.i18n.inputLabel)}
                  </label>
                  <input
                    aria-describedby={showAddressError ? 'induction-address-error' : undefined}
                    aria-invalid={!!showAddressError}
                    id="induction-address"
                    onBlur={() => setIsAddressTouched(true)}
                    onChange={(event) => setAddress(event.target.value)}
                    inputMode={addressStep.inputMode}
                    placeholder={_(addressStep.i18n.placeholder)}
                    type={addressStep.inputType}
                    value={address}
                  />
                  {showAddressError && (
                    <div className="dp-message dp-error-form" role="alert">
                      <p id="induction-address-error">{_(addressError)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <InductionFooter
              acceptDisabled={!!addressError}
              acceptLabel={_(addressStep.i18n.accept)}
              backLabel={_(addressStep.i18n.back)}
              onAccept={() => goToNextStep(addressStepDefinition.nextStep.name)}
              onBack={() => setCurrentStage(channelSelectionStep.name)}
              stepLabel={_('induction.step_1_of_2')}
            />
          </section>
        ) : (
          <section
            className="dp-library"
            data-testid="induction-goals-step"
            aria-labelledby="induction-goals-title"
          >
            <h2 id="induction-goals-title" className="dp-second-order-title">
              {_(goalsStep.i18n.title)}
            </h2>
            <p id="induction-goals-description" className="dp-color-grey m-b-24">
              {_(goalsStep.i18n.description)}
            </p>
            <fieldset
              aria-describedby="induction-goals-description"
              aria-label={_(goalsStep.i18n.title)}
              className="dp-rowflex m-t-30 m-b-24"
            >
              {goalsStep.options.map(({ iconClass, id, i18n }) => (
                <div key={id} className="col-sm-12 col-md-6 col-lg-6 m-b-12">
                  <label className="dp-selectable-option">
                    <input
                      checked={selectedGoals.includes(id)}
                      className="dp-selectable-option__input"
                      id={`induction-goal-${id}`}
                      name="induction-goals"
                      onChange={() => toggleGoal(id)}
                      type="checkbox"
                      value={id}
                    />
                    <span className="dp-selectable-option__content">
                      <span className="dp-icon-tile dp-icon-tile--square">
                        <i aria-hidden="true" className={iconClass} />
                      </span>
                      <span className="dp-selectable-option__label">{_(i18n.label)}</span>
                      <span className="dp-selectable-option__check" aria-hidden="true">
                        <i className="icon-checkmark" />
                      </span>
                    </span>
                  </label>
                </div>
              ))}
            </fieldset>
            <InductionFooter
              acceptDisabled={!selectedGoals.length}
              acceptLabel={_(goalsStep.i18n.accept)}
              backLabel={_(goalsStep.i18n.back)}
              isSecondStep
              onAccept={() => setIsModalOpen(false)}
              onBack={() => setCurrentStage(previousStage)}
              stepLabel={_('induction.step_2_of_2')}
            />
          </section>
        )}
      </Modal>
    </>
  );
};

export const Induction = InjectAppServices(InductionComponent);

const InductionOnboarding = ({ config, formatMessage }) => {
  const [completedTaskIds, setCompletedTaskIds] = useState([]);
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const completedTasks = completedTaskIds.length;
  const completeTask = (taskId) => {
    setCompletedTaskIds((currentTaskIds) =>
      currentTaskIds.includes(taskId) ? currentTaskIds : [...currentTaskIds, taskId],
    );
  };

  return (
    <main className="dp-library" data-testid="induction-onboarding">
      <div className="dp-container m-t-48 m-b-48">
        <div className="dp-rowflex p-l-12">
          <div className="col-sm-12 col-md-12 col-lg-12">
            <section className="dp-box-shadow" aria-labelledby="induction-onboarding-title">
              <div className="dp-rowflex">
                <aside className="col-sm-12 col-md-3 col-lg-3">
                  <div className="dp-colflex dp-justify-content-between dp-green-brightness p-t-36 p-r-30 p-b-36 p-l-30">
                    <div>
                      <h1 id="induction-onboarding-title" className="dp-second-order-title">
                        {formatMessage(config.i18n.title)}
                      </h1>
                      <p className="dp-color-grey">{formatMessage(config.i18n.description)}</p>
                      <p className="uppercase m-t-30 m-b-12">
                        {formatMessage(config.i18n.progress, {
                          completed: completedTasks,
                          total: config.milestones.length,
                        })}
                      </p>
                      <div className="dp-widget-plan-progress">
                        <div
                          aria-label={formatMessage(config.i18n.progress, {
                            completed: completedTasks,
                            total: config.milestones.length,
                          })}
                          aria-valuemax={config.milestones.length}
                          aria-valuemin="0"
                          aria-valuenow={completedTasks}
                          className="dp-progress-bar"
                          role="progressbar"
                        >
                          <div
                            className="progress"
                            style={{
                              width: `${(completedTasks / config.milestones.length) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <p className="dp-color-grey">
                      {formatMessage(config.i18n.helpBeforeLink)}{' '}
                      <a
                        href="https://help.fromdoppler.com/es"
                        onClick={(event) => {
                          if (openZendeskChat()) {
                            event.preventDefault();
                          }
                        }}
                      >
                        {formatMessage(config.i18n.helpLink)}
                      </a>{' '}
                      {formatMessage(config.i18n.helpAfterLink)}
                    </p>
                  </div>
                </aside>
                <section
                  aria-label={formatMessage(config.i18n.tasksLabel)}
                  className="col-sm-12 col-md-9 col-lg-9 p-t-12 p-r-36 p-b-12 p-l-36"
                >
                  {config.milestones.map(({ action, iconClass, id, i18n }) => {
                    const isCompleted = completedTaskIds.includes(id);
                    const hasTutorial = i18n.tutorialText && i18n.tutorialLink;

                    return (
                      <article className="dp-onboarding-task" key={id}>
                        <span
                          className={`dp-icon-tile dp-icon-tile--circle${
                            isCompleted ? ' dp-icon-tile--active' : ''
                          }`}
                        >
                          <i aria-hidden="true" className={iconClass} />
                        </span>
                        <div className="dp-onboarding-task__content">
                          <h2
                            className={`dp-onboarding-task__title${
                              isCompleted ? ' dp-onboarding-task__title--completed' : ''
                            }`}
                          >
                            {formatMessage(i18n.title)}
                          </h2>
                          <p className="dp-onboarding-task__description">
                            {formatMessage(i18n.description)}
                          </p>
                          {isCompleted ? (
                            <>
                              <div
                                className="dp-onboarding-task__success col-sm-12 col-md-8 col-lg-8"
                                role="status"
                              >
                                <i aria-hidden="true" className="icon-checkmark" />
                                {formatMessage(i18n.success)}
                              </div>
                              {hasTutorial && (
                                <span className="dp-onboarding-task__tutorial">
                                  {formatMessage(i18n.tutorialText)}{' '}
                                  <a
                                    className="dp-onboarding-task__tutorial-link"
                                    href="#induction-tutorial"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      setIsTutorialModalOpen(true);
                                    }}
                                  >
                                    {formatMessage(i18n.tutorialLink)}
                                  </a>
                                </span>
                              )}
                            </>
                          ) : (
                            <div className="dp-onboarding-task__actions">
                              <a
                                className="dp-button button-medium primary-green"
                                href={formatMessage(action.urlKey)}
                              >
                                {formatMessage(i18n.label)}
                              </a>
                              <button
                                className="dp-onboarding-task__complete-action"
                                onClick={() => completeTask(id)}
                                type="button"
                              >
                                <i aria-hidden="true" className="icon-checkmark" />
                                {formatMessage(config.i18n.complete)}
                              </button>
                            </div>
                          )}
                        </div>
                      </article>
                    );
                  })}
                  <div
                    aria-hidden="true"
                    className="p-t-84 p-b-84"
                    data-testid="induction-third-task-space"
                  />
                  {completedTasks === config.milestones.length && (
                    <button className="dp-button button-medium ctaTertiary m-l-60" type="button">
                      {formatMessage(config.i18n.finish)}
                    </button>
                  )}
                </section>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Modal
        isCenter
        isOpen={isTutorialModalOpen}
        modalId="induction-tutorial-modal"
        type="medium"
        handleClose={() => setIsTutorialModalOpen(false)}
      >
        <div />
      </Modal>
    </main>
  );
};

const InductionFooter = ({
  acceptDisabled,
  acceptLabel,
  backLabel,
  isSecondStep = false,
  onAccept,
  onBack,
  stepLabel,
}) => (
  <div className="dp-rowflex">
    <div className="col-sm-12 col-md-12 col-lg-12">
      <div className="dp-flex dp-justify-content-between p-r-24">
        <span className="dp-step-indicator" role="img" aria-label={stepLabel}>
          <span className="dp-step-indicator__item dp-step-indicator__item--current" />
          <span
            className={`dp-step-indicator__item${
              isSecondStep ? ' dp-step-indicator__item--current' : ''
            }`}
          />
        </span>
        <div>
          {backLabel && (
            <button className="dp-button dp-color-lightgrey m-r-18" onClick={onBack} type="button">
              {backLabel}
            </button>
          )}
          <button
            className="dp-button button-medium primary-green"
            disabled={acceptDisabled}
            onClick={onAccept}
            type="button"
          >
            {acceptLabel}
          </button>
        </div>
      </div>
    </div>
  </div>
);
